// ==========================================================================
// APP.JS - CORES CLIENT ENGINE & CALCULATOR FOR WURUPAYROLL PWA
// ==========================================================================

import {
  collection, query, where, getDocs, getDoc, doc, setDoc
} from "firebase/firestore";

import {
  db, storage, auth, getCompanySettings, saveCompanySettings, getEmployees, addEmployee,
  updateEmployee, deleteEmployee, getResignedArchiveEmployees, getTimesheet,
  saveTimesheet, getCalendar, saveCalendar, uploadTransferSlip, uploadEmployeeDocument,
  deleteEmployeeAttachment, getAttachmentBlob, initializeDefaultDataIfNeeded, getStorageUsage,
  getPublicHolidaysCache, savePublicHolidaysCache, cleanUpOldHolidaysCache, cleanGarbageTimesheets,
  clearTimesheetCache
} from "./firebase-db.js";

import { escapeHtml, escapeAttr, escapeJsParam } from "./utils/sanitize.js";
import { showToast, toast, showConfirm } from "./utils/toast.js";
import {
  getEmployeeWorkHoursStr,
  isEmployeeActiveInMonth,
  getEmployeeDeductionSettings,
  parseWorkHoursRange,
  calculatePayrollForEmployee,
  calculateMaxAdvanceAllowed,
  finalizeTimesheetDays,
  convertToThaiBahtText
} from "./modules/payroll-calc.js";

import {
  renderCalendarPage,
  updateCalendarYear,
  switchCalendarMode,
  updateCalendarStatsCounters,
  renderRowCalendarList,
  renderYearlyCalendarGrid,
  changeMonthlyMonth,
  fetchThaiPublicHolidays,
  renderMonthlyCalendarGrid,
  showDayEventAction,
  showEmployeeHolidayBookingModal,
  submitEmployeeHolidayBooking,
  cancelEmployeeHolidayTrigger,
  getDefaultDayStatus,
  showAddHolidayModal,
  showEditHolidayModal,
  showHolidayFormModal,
  deleteCalendarEventFromModalTrigger,
  toggleCompensatoryOffField,
  submitCalendarEventTrigger,
  deleteCalendarEventTrigger
} from "./modules/calendar.js";

import {
  renderReportsPage,
  switchReportTab,
  renderBankSummaryReport,
  cleanGarbageTimesheetsTrigger,
  copyFinanceSummaryToClipboard,
  uploadSlipTrigger,
  showPayslipModal,
  printPayslipDirectly,
  renderSocialSecurityMonthlyReport,
  triggerMarkSSPaid,
  triggerUnmarkSSPaid,
  triggerMarkAllSSPaid,
  triggerUnmarkAllSSPaid,
  renderWithholdingTaxReport,
  renderKT26AnnualReport,
  createPrintIframe,
  printTransferSummaryReport,
  printSSOReport,
  printTaxReport,
  printKT26kReport,
  printMasterPayrollReport,
  renderMasterPayrollReport
} from "./modules/reports.js";

import {
  renderSettingsPage,
  toggleDayWorkHours,
  toggleDiligenceAllowanceSection,
  updateSelectedPayor,
  showManagePayorsModal,
  addNewPayorTrigger,
  deletePayorItem,
  renderPermissionsPage,
  savePermissions
} from "./modules/settings.js";

import {
  renderEmployeesPage,
  handleEmpDragStart,
  handleEmpDragOver,
  handleEmpDragLeave,
  handleEmpDrop,
  handleEmpDragEnd,
  switchEmployeeTab,
  toggleReorderMode,
  showAddEmployeeModal,
  editEmployee,
  toggleCustomTaxRate,
  toggleCustomSSRate,
  toggleInternStatus,
  toggleDiligenceFields,
  toggleCustomDiligenceAmount,
  renderEmployeeFormModal,
  toggleEmployeeTypeFields,
  toggleSecurityDepositFields,
  toggleLoanFields,
  toggleResignFields,
  toggleEmployeeStatusFields,
  toggleLoginAccountFields,
  addNewRecurringRow,
  addTimesheetAdjustmentRow,
  closeEmployeeModal,
  submitEmployeeForm,
  showResignedArchiveModal,
  triggerHardDeleteEmployee,
  viewEmployeeDetails,
  uploadEmployeeDocTrigger,
  openDisciplinaryModal,
  saveDisciplinaryRecord,
  deleteDisciplinaryRecord,
  printDisciplinaryLetter,
  removeEmployeeAttachment,
  toggleBubbleMenu,
  downloadDocumentAs
} from "./modules/employees.js";

import {
  renderAttendancePage,
  openEmployeeTimesheetPage,
  goBackFromTimesheet,
  initRetroactiveTimesheet,
  renderTimesheetPage,
  onGridStatusChange,
  recalculateGridTotals,
  validateDayAdvance,
  saveTimesheetTrigger,
  lockTimesheetTrigger,
  unlockTimesheetTrigger,
  updateQuickDailyTimes,
  calculateQuickDailyEarnings,
  openDailyRecordModal,
  saveQuickDailyRecordTrigger,
  printEmployeeTimesheetReport
} from "./modules/timesheets.js";

import {
  renderLoginPage,
  showForgotPasswordModal,
  submitPasswordReset,
  logout
} from "./modules/auth.js";

import {
  autoConfirmAllEmployeesTimesheets,
  triggerLockAllTimesheets,
  renderDashboard
} from "./modules/dashboard.js";

export {
  getEmployeeWorkHoursStr,
  isEmployeeActiveInMonth,
  getEmployeeDeductionSettings,
  parseWorkHoursRange,
  calculatePayrollForEmployee,
  calculateMaxAdvanceAllowed,
  finalizeTimesheetDays,
  convertToThaiBahtText,
  renderCalendarPage,
  updateCalendarYear,
  switchCalendarMode,
  updateCalendarStatsCounters,
  renderRowCalendarList,
  renderYearlyCalendarGrid,
  changeMonthlyMonth,
  fetchThaiPublicHolidays,
  renderMonthlyCalendarGrid,
  showDayEventAction,
  showEmployeeHolidayBookingModal,
  submitEmployeeHolidayBooking,
  cancelEmployeeHolidayTrigger,
  getDefaultDayStatus,
  showAddHolidayModal,
  showEditHolidayModal,
  showHolidayFormModal,
  deleteCalendarEventFromModalTrigger,
  toggleCompensatoryOffField,
  submitCalendarEventTrigger,
  deleteCalendarEventTrigger,
  renderReportsPage,
  switchReportTab,
  renderBankSummaryReport,
  cleanGarbageTimesheetsTrigger,
  copyFinanceSummaryToClipboard,
  uploadSlipTrigger,
  showPayslipModal,
  printPayslipDirectly,
  renderSocialSecurityMonthlyReport,
  triggerMarkSSPaid,
  triggerUnmarkSSPaid,
  triggerMarkAllSSPaid,
  triggerUnmarkAllSSPaid,
  renderWithholdingTaxReport,
  renderKT26AnnualReport,
  createPrintIframe,
  printTransferSummaryReport,
  printSSOReport,
  printTaxReport,
  printKT26kReport,
  printMasterPayrollReport,
  renderMasterPayrollReport,
  renderSettingsPage,
  toggleDayWorkHours,
  toggleDiligenceAllowanceSection,
  updateSelectedPayor,
  showManagePayorsModal,
  addNewPayorTrigger,
  deletePayorItem,
  renderPermissionsPage,
  savePermissions,
  renderEmployeesPage,
  handleEmpDragStart,
  handleEmpDragOver,
  handleEmpDragLeave,
  handleEmpDrop,
  handleEmpDragEnd,
  switchEmployeeTab,
  toggleReorderMode,
  openEmployeeTimesheetPage,
  showAddEmployeeModal,
  editEmployee,
  toggleCustomTaxRate,
  toggleCustomSSRate,
  toggleInternStatus,
  toggleDiligenceFields,
  toggleCustomDiligenceAmount,
  renderEmployeeFormModal,
  toggleEmployeeTypeFields,
  toggleSecurityDepositFields,
  toggleLoanFields,
  toggleResignFields,
  toggleLoginAccountFields,
  addNewRecurringRow,
  addTimesheetAdjustmentRow,
  closeEmployeeModal,
  submitEmployeeForm,
  showResignedArchiveModal,
  triggerHardDeleteEmployee,
  viewEmployeeDetails,
  uploadEmployeeDocTrigger,
  openDisciplinaryModal,
  saveDisciplinaryRecord,
  deleteDisciplinaryRecord,
  printDisciplinaryLetter,
  removeEmployeeAttachment,
  toggleBubbleMenu,
  downloadDocumentAs,
  renderAttendancePage,
  goBackFromTimesheet,
  initRetroactiveTimesheet,
  renderTimesheetPage,
  onGridStatusChange,
  recalculateGridTotals,
  validateDayAdvance,
  saveTimesheetTrigger,
  lockTimesheetTrigger,
  unlockTimesheetTrigger,
  updateQuickDailyTimes,
  calculateQuickDailyEarnings,
  openDailyRecordModal,
  saveQuickDailyRecordTrigger,
  printEmployeeTimesheetReport,
  renderLoginPage,
  showForgotPasswordModal,
  submitPasswordReset,
  logout,
  autoConfirmAllEmployeesTimesheets,
  triggerLockAllTimesheets,
  renderDashboard
};

// Global Application State
const state = {
  currentUser: null,
  activeCompanyId: 'itembox', // Default company
  activeCompanySettings: null,
  activePage: 'login',
  selectedMonth: new Date().toISOString().slice(0, 7), // YYYY-MM (e.g. 2026-05)
  employees: [],
  holidays: [],
  specialWorkdays: [],
  employeeHolidays: [],
  currentCalendarYear: new Date().getFullYear(),
  selectedPayor: "", // Selected authorized payor for payslips
  isReorderMode: false,
  activeReportTab: 'bank',
  activeCalendarMode: 'yearly',
  activeEmployeeTab: 'active',
  activeEditingEmployeeId: null,
  timesheetReturnPage: 'dashboard',
  activePayslipEmployeeId: null,
  rolePermissions: null,
  fetchedPublicHolidays: {},
  currentMonthlyMonth: undefined,
  isFetchingHolidays: false
};
window.state = state;
window.printMasterPayrollReport = printMasterPayrollReport;
window.renderMasterPayrollReport = renderMasterPayrollReport;
window.printEmployeeTimesheetReport = printEmployeeTimesheetReport;

// Default Permissions Matrix (if not yet saved in Firebase)
const defaultRolePermissions = {
  superadmin: {
    access_all_companies: true,
    view_dashboard: true,
    view_calendar: true,
    view_others_leave_calendar: true,

    view_employees: true,
    add_employee: true,
    delete_employee: true,
    reorder_employees: true,
    manage_employee_files: true,
    manage_disciplinary: true,
    edit_employee_general: true,
    edit_employee_salary: true,
    view_employee_tenure: true,
    view_employee_salary_increase: true,

    view_timesheet: true,
    edit_timesheet: true,
    edit_payroll_adjustments: true,
    lock_timesheet: true,
    unlock_timesheet: true,
    view_past_months_limit: 99,
    edit_past_months_limit: 99,
    view_future_months_limit: 99,
    edit_future_months_limit: 99,

    view_reports: true,
    export_payslips: true,
    export_transfer_report: true,
    export_sso_report: true,
    export_tax_report: true,
    export_kt26k_report: true,

    manage_company_settings: true,
    manage_holidays: true,
    manage_retroactive_holidays: true,

    manage_roles_permissions: true,
    assign_employee_roles: true
  },
  admin: {
    access_all_companies: true,
    view_dashboard: true,
    view_calendar: true,
    view_others_leave_calendar: true,

    view_employees: true,
    add_employee: true,
    delete_employee: false,
    reorder_employees: true,
    manage_employee_files: true,
    manage_disciplinary: true,
    edit_employee_general: true,
    edit_employee_salary: true,
    view_employee_tenure: true,
    view_employee_salary_increase: true,

    view_timesheet: true,
    edit_timesheet: true,
    edit_payroll_adjustments: true,
    lock_timesheet: true,
    unlock_timesheet: true,
    view_past_months_limit: 1,
    edit_past_months_limit: 1,
    view_future_months_limit: 0,
    edit_future_months_limit: 0,

    view_reports: true,
    export_payslips: true,
    export_transfer_report: true,
    export_sso_report: true,
    export_tax_report: true,
    export_kt26k_report: true,

    manage_company_settings: true,
    manage_holidays: true,
    manage_retroactive_holidays: false,

    manage_roles_permissions: false,
    assign_employee_roles: false
  },
  member: {
    access_all_companies: false,
    view_dashboard: true,
    view_calendar: true,
    view_others_leave_calendar: false,

    view_employees: false,
    add_employee: false,
    delete_employee: false,
    reorder_employees: false,
    manage_employee_files: false,
    manage_disciplinary: false,
    edit_employee_general: false,
    edit_employee_salary: false,
    view_employee_tenure: false,
    view_employee_salary_increase: false,

    view_timesheet: true, // Specific code will restrict this to ONLY their own timesheet
    edit_timesheet: false,
    edit_payroll_adjustments: false,
    lock_timesheet: false,
    unlock_timesheet: false,
    view_past_months_limit: 0,
    edit_past_months_limit: 0,
    view_future_months_limit: 0,
    edit_future_months_limit: 0,

    view_reports: false,
    export_payslips: false,
    export_transfer_report: false,
    export_sso_report: false,
    export_tax_report: false,
    export_kt26k_report: false,

    manage_company_settings: false,
    manage_holidays: false,
    manage_retroactive_holidays: false,

    manage_roles_permissions: false,
    assign_employee_roles: false
  }
};

// ==========================================================================
// APP LIFE-CYCLE & INITIALIZATION
// ==========================================================================
async function initApp() {
  // 1. Ensure master admin & mock database seeds exist
  try {
    const savedUserStr = localStorage.getItem('wuru_user');
    if (savedUserStr) {
      await initializeDefaultDataIfNeeded();
    }
  } catch (err) {
    console.error("Firebase connection error during seeding:", err);
  }

  // 2. Register Service Worker for PWA
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('/sw.js', { scope: '/' })
      .then((reg) => {
        console.log('[PWA] Service Worker registered:', reg.scope);
      })
      .catch((err) => {
        console.warn('[PWA] Service Worker registration failed:', err);
      });
  }


  // 3. Setup UI Interaction listeners (Hamburger, switches, modal close etc)
  setupGlobalListeners();

  // 4. Initial route check or load session
  await checkLocalSession();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initApp);
} else {
  initApp();
}

function setupGlobalListeners() {
  // Handle sidebar links clicking
  document.querySelectorAll('.nav-links li a').forEach(link => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      const targetPage = link.getAttribute('data-page');
      if (targetPage) {
        navigateTo(targetPage);
      }
    });
  });

  // Handle Hamburger Sidebar Toggle for Mobile is now handled via window.toggleSidebar()
  window.toggleSidebar = function () {
    const sidebar = document.querySelector('.sidebar');
    if (sidebar) {
      sidebar.classList.toggle('active');
    }
  };

  // Close sidebar on link click (mobile)
  document.querySelectorAll('.nav-links li a').forEach(link => {
    link.addEventListener('click', () => {
      if (window.innerWidth <= 1024) {
        document.querySelector('.sidebar').classList.remove('active');
      }
    });
  });
}

// Check if user is already logged in locally
async function checkLocalSession() {
  const savedUserStr = localStorage.getItem('wuru_user');
  let savedComp = localStorage.getItem('wuru_active_company');

  if (savedUserStr) {
    state.currentUser = JSON.parse(savedUserStr);

    // Load RBAC permissions from Firebase
    await loadGlobalPermissions();

    const isMasterAdmin = state.currentUser.id === 'emp_master' || state.currentUser.role === 'admin' || state.currentUser.role === 'superadmin';

    if (isMasterAdmin && !sessionStorage.getItem('wuru_session_started')) {
      savedComp = 'itembox';
      localStorage.setItem('wuru_active_company', 'itembox');
      sessionStorage.setItem('wuru_session_started', 'true');
    } else if (!sessionStorage.getItem('wuru_session_started')) {
      sessionStorage.setItem('wuru_session_started', 'true');
    }

    if (savedComp) {
      state.activeCompanyId = savedComp;
    }
    document.body.style.display = 'flex'; // Unhide UI
    setupThemeBranding();

    // Rebuild full app shell HTML to render sidebar before navigating
    rebuildAppShellHTML();

    navigateTo('dashboard');
  } else {
    renderLoginPage();
  }
}

// Toggle active company
window.switchCompany = async function (companyId) {
  clearTimesheetCache();
  state.activeCompanyId = companyId;
  localStorage.setItem('wuru_active_company', companyId);
  setupThemeBranding();
  await loadCompanyContext();
  navigateTo(state.activePage);
};

// Set color branding on body
window.setupThemeBranding = setupThemeBranding;
function setupThemeBranding() {
  document.body.className = ''; // Reset
  if (state.activeCompanyId === 'itembox') {
    document.body.classList.add('theme-peach-cream');
  } else {
    document.body.classList.add('theme-cool-grey');
  }
}

// Load company details & employees
async function loadCompanyContext(forceLoadHolidays = false) {
  const settingsPromise = getCompanySettings(state.activeCompanyId);
  const employeesPromise = getEmployees(state.activeCompanyId);
  const calendarPromise = getCalendar(state.activeCompanyId, state.currentCalendarYear);

  const [settings, fetchedEmployees, calendarData] = await Promise.all([
    settingsPromise,
    employeesPromise,
    calendarPromise
  ]);

  state.activeCompanySettings = settings;

  fetchedEmployees.sort((a, b) => {
    const orderA = a.sortOrder !== undefined ? a.sortOrder : 999999;
    const orderB = b.sortOrder !== undefined ? b.sortOrder : 999999;
    return orderA - orderB;
  });
  state.employees = fetchedEmployees;

  state.holidays = calendarData.holidays || [];
  state.specialWorkdays = calendarData.specialWorkdays || [];

  state.employeeHolidays = [];
  const isCalendarPage = state.activePage === 'calendar' || forceLoadHolidays;

  if (isCalendarPage && state.activeCompanySettings && state.activeCompanySettings.holidaySystem === 'employee_selected') {
    try {
      const yearStr = String(state.currentCalendarYear);
      const q = query(
        collection(db, 'timesheets'),
        where('month', '>=', `${yearStr}-01`),
        where('month', '<=', `${yearStr}-12`)
      );
      const snap = await getDocs(q);
      const employeeIds = new Set(state.employees.map(e => e.id));

      const isEmployee = state.currentUser && state.currentUser.role === 'employee';
      const allowOthers = state.activeCompanySettings && state.activeCompanySettings.allowEmployeesToViewOthersLeave !== false;

      snap.forEach(doc => {
        const ts = doc.data();
        if (employeeIds.has(ts.employeeId)) {
          // Filter out other employees' holidays if standard employee and settings block viewing
          if (isEmployee && !allowOthers && ts.employeeId !== state.currentUser.id) {
            return;
          }

          const emp = state.employees.find(e => e.id === ts.employeeId);
          const empName = emp ? (emp.nickname || (emp.realName ? emp.realName.split(' ')[0] : 'พนักงาน')) : 'พนักงาน';

          if (ts.days) {
            for (const d in ts.days) {
              if (ts.days[d].status === 'holiday_selected') {
                const dateStr = `${ts.month}-${String(d).padStart(2, '0')}`;
                state.employeeHolidays.push({
                  date: dateStr,
                  employeeId: ts.employeeId,
                  employeeName: empName,
                  name: `วันหยุดประจำปี: ${empName}`
                });
              }
            }
          }
        }
      });
    } catch (err) {
      console.error("Error loading employee holidays:", err);
    }
  }
}
window.loadCompanyContext = loadCompanyContext;
export { loadCompanyContext };

// ==========================================================================
// ROLE ROUTE GUARDS & NAVIGATION
// ==========================================================================
export async function navigateTo(pageId) {
  state.activePage = pageId;
  state.isReorderMode = false;

  // Highlight active nav link
  document.querySelectorAll('.nav-links li').forEach(li => {
    const link = li.querySelector('a');
    if (link && link.getAttribute('data-page') === pageId) {
      li.classList.add('active');
    } else {
      li.classList.remove('active');
    }
  });

  // Verify authorization & routing constraints
  if (!state.currentUser) {
    renderLoginPage();
    return;
  }

  // Load contextual data first
  await loadCompanyContext();

  // Render specific layout container
  const main = document.getElementById('main-container');
  if (!main) return;
  main.innerHTML = getHeaderHTML() + `<div id="page-content-wrapper"></div>`;

  // Apply authorization checks (Requirement 16)
  const isAuthorized = checkPagePermission(pageId);
  if (!isAuthorized) {
    document.getElementById('page-content-wrapper').innerHTML = `
      <div class="glass-card text-center" style="margin-top: 50px; padding: 50px;">
        <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 24 24" fill="#e63946" style="margin-bottom: 20px;">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
        </svg>
        <h2 style="color: var(--danger); font-weight: 700;">ขออภัย! คุณไม่มีสิทธิ์เข้าถึงหน้านี้</h2>
        <p style="color: var(--text-sub); margin-top: 10px;">บัญชีของคุณไม่มีสิทธิ์ในการดูหรือจัดการส่วนนี้ของบริษัท ${escapeHtml(state.activeCompanySettings.name)}</p>
      </div>
    `;
    return;
  }

  // Trigger page renders
  switch (pageId) {
    case 'dashboard':
      renderDashboard();
      break;
    case 'employees':
      renderEmployeesPage();
      break;
    case 'timesheet':
      renderTimesheetPage();
      break;
    case 'attendance':
      renderAttendancePage();
      break;
    case 'calendar':
      renderCalendarPage();
      break;
    case 'reports':
      renderReportsPage();
      break;
    case 'settings':
      renderSettingsPage();
      break;
    case 'permissions':
      if (window.checkPermission('manage_roles_permissions')) {
        renderPermissionsPage();
      } else {
        navigateTo('dashboard');
      }
      break;
  }
}
window.navigateTo = navigateTo;


// Role-based Access Control checks (Requirement 16 & 18)
function checkPagePermission(pageId) {
  // Master Admin (May) has unrestricted access to EVERYTHING (Requirement 18)
  if (state.currentUser.id === 'emp_master') {
    return true;
  }

  // Verify company scope first
  if (!window.checkPermission('access_all_companies')) {
    if (state.currentUser.companyId && state.currentUser.companyId !== state.activeCompanyId) {
      return false;
    }
  }

  // Check page-specific permissions via RBAC Matrix
  switch (pageId) {
    case 'dashboard':
      return window.checkPermission('view_dashboard');
    case 'employees':
      return window.checkPermission('view_employees');
    case 'timesheet':
    case 'attendance':
      return window.checkPermission('view_timesheet');
    case 'calendar':
      return window.checkPermission('view_calendar');
    case 'reports':
      return window.checkPermission('view_reports');
    case 'settings':
      return window.checkPermission('manage_company_settings');
    case 'permissions':
      return window.checkPermission('manage_roles_permissions');
    default:
      return false;
  }
}

// Helper to mask executive salary
window.canViewSalary = function (emp) {
  if (!emp) return true;
  if (emp.department === 'บริหาร') {
    return window.checkPermission('view_executive_salary');
  }
  return true;
};

// Check detailed RBAC permissions dynamically
window.checkPermission = function (action) {
  if (!state.currentUser) return false;
  let role = state.currentUser.role || 'member';

  // Hardcode for master or specific name during transition
  if (state.currentUser.id === 'emp_master' || state.currentUser.realName === 'นัททราย ดุสิตตานครินทร์') {
    role = 'superadmin';
  }

  // Legacy role mapping
  if (role === 'employee' || role === 'viewer_a') role = 'member';
  if (role === 'manager_a') role = 'admin';

  if (!state.rolePermissions || !state.rolePermissions[role]) return false;
  return state.rolePermissions[role][action];
};

async function loadGlobalPermissions() {
  try {
    const docRef = doc(db, 'globalSettings', 'permissions');
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      state.rolePermissions = docSnap.data();
    } else {
      // Create defaults
      state.rolePermissions = JSON.parse(JSON.stringify(defaultRolePermissions));
      await setDoc(docRef, state.rolePermissions);
    }
  } catch (err) {
    console.error("Error loading permissions:", err);
    state.rolePermissions = JSON.parse(JSON.stringify(defaultRolePermissions));
  }
}



// ==========================================================================
// AUTHENTICATION & LOGIN (Extracted to src/modules/auth.js)
// ==========================================================================

window.rebuildAppShellHTML = rebuildAppShellHTML;
function rebuildAppShellHTML() {
  document.body.innerHTML = `
    <!-- Sidebar -->
    <div class="sidebar">
      <div class="brand-area">
        <img src="icon-512.png" alt="Logo" class="brand-icon">
        <span class="brand-title">WuRu Payroll</span>
      </div>
      <ul class="nav-links">
        ${window.checkPermission('view_dashboard') ? `
        <li class="active"><a data-page="dashboard">
          <svg viewBox="0 0 24 24"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></svg> หน้าหลัก</a>
        </li>
        ` : ''}
        ${window.checkPermission('view_employees') ? `
        <li><a data-page="employees">
          <svg viewBox="0 0 24 24"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg> ข้อมูลพนักงาน</a>
        </li>
        ` : ''}
        ${window.checkPermission('view_calendar') ? `
        <li><a data-page="calendar">
          <svg viewBox="0 0 24 24"><path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.11-.9-2-2-2zm0 16H5V10h14v10zm-5-8h-2v2h2v-2zm-4 0H8v2h2v-2zm-4 0h2v-2H6v2zm10 0h-2v-2h2v2zm-4 0h-2V8h2v2zm-4 0h2V8H6v2z"/></svg> ปฏิทินวันหยุด</a>
        </li>
        ` : ''}
        ${window.checkPermission('view_reports') ? `
        <li><a data-page="reports">
          <svg viewBox="0 0 24 24"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/></svg> รายงานเงินเดือน</a>
        </li>
        ` : ''}
        ${window.checkPermission('manage_company_settings') ? `
        <li><a data-page="settings">
          <svg viewBox="0 0 24 24"><path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/></svg> ตั้งค่าระบบ</a>
        </li>
        ` : ''}
        ${window.checkPermission('manage_roles_permissions') ? `
        <li><a data-page="permissions">
          <svg viewBox="0 0 24 24"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/></svg> ตั้งค่าสิทธิ์</a>
        </li>
        ` : ''}
      </ul>
      <div class="nav-footer">
        <div class="user-profile">
          <div class="user-info">
            <span class="user-name" id="profile-name">May</span>
            <div class="user-role" id="profile-role">Master Admin</div>
          </div>
          <button class="btn-logout" id="profile-logout-btn" title="Log Out">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M13 3h-2v10h2V3zm4.78 2.22l-1.4 1.4C17.9 7.8 19 9.77 19 12c0 3.87-3.13 7-7 7s-7-3.13-7-7c0-2.23 1.1-4.2 2.62-5.38l-1.4-1.4C4.28 6.96 3 9.33 3 12c0 4.97 4.03 9 9 9s9-4.03 9-9c0-2.67-1.28-5.04-3.22-6.78z"/></svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Sidebar Mobile Overlay -->
    <div class="sidebar-overlay" onclick="window.toggleSidebar()"></div>

    <!-- Main Content Container -->
    <div class="main-content" id="main-container">
      <!-- Pages will render dynamically here -->
    </div>
  `;
  setupGlobalListeners();

  // Bind Logout Button
  document.getElementById('profile-logout-btn').addEventListener('click', () => {
    localStorage.removeItem('wuru_user');
    localStorage.removeItem('wuru_active_company');
    state.currentUser = null;
    renderLoginPage();
  });
}

// Generate header HTML
function getHeaderHTML() {
  const isMasterAdmin = state.currentUser.id === 'emp_master' || state.currentUser.role === 'admin' || state.currentUser.role === 'superadmin';
  let companyToggleHTML = '';

  if (isMasterAdmin) {
    companyToggleHTML = `
      <div class="company-selector">
        <button class="company-toggle-btn ${state.activeCompanyId === 'itembox' ? 'active' : ''}" onclick="switchCompany('itembox')">ITEMBOX</button>
        <button class="company-toggle-btn ${state.activeCompanyId !== 'itembox' ? 'active' : ''}" onclick="switchCompany('second_co')">MEANBOOK</button>
      </div>
    `;
  } else {
    companyToggleHTML = `
      <div style="background: var(--card-bg); padding: 10px 18px; border-radius: var(--border-radius-md); border: 1px solid var(--card-border); font-weight: 700; color: var(--primary-dark);">
        🏢 ${escapeHtml(state.activeCompanySettings.name)}
      </div>
    `;
  }

  // Bind profile details in sidebar
  setTimeout(() => {
    const profName = document.getElementById('profile-name');
    const profRole = document.getElementById('profile-role');
    if (profName && profRole) {
      profName.textContent = state.currentUser.nickname || (state.currentUser.realName ? state.currentUser.realName.split(' ')[0] : 'พนักงาน');

      let roleThai = 'พนักงาน';
      if (state.currentUser.role === 'superadmin') roleThai = 'ผู้ดูแลระบบสูงสุด';
      else if (state.currentUser.role === 'admin') roleThai = 'ผู้ดูแลระบบ';
      else if (state.currentUser.role === 'manager_a') roleThai = 'ผู้จัดการ';
      else if (state.currentUser.role === 'viewer_a') roleThai = 'ผู้เข้าชม';

      profRole.textContent = roleThai;
    }
  }, 100);

  const thaiMonthsArr = ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'];
  const [sYear, sMonth] = state.selectedMonth.split('-');
  const displayMonthStr = `${thaiMonthsArr[parseInt(sMonth) - 1]} ${sYear}`;

  return `
    <div class="app-header">
      <button class="hamburger-menu" onclick="window.toggleSidebar()">
        <svg viewBox="0 0 24 24"><path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/></svg>
      </button>
      <div class="page-title-group">
        <h1 id="page-display-title">หน้าหลัก</h1>
        <p id="page-display-desc">แดชบอร์ดสรุปและเมนูควบคุมแอป</p>
      </div>
      <div class="header-actions">
        <div style="display: flex; align-items: center; gap: 8px;">
          <button onclick="goToCurrentMonth()" style="background: #eef2fa; color: var(--primary-dark); border: 1px solid #d0d7e6; padding: 7px 12px; border-radius: var(--border-radius-sm); font-size: 0.85rem; font-weight: 700; cursor: pointer; box-shadow: 0 1px 2px rgba(0,0,0,0.05); display: flex; align-items: center; gap: 4px; transition: all 0.2s;" onmouseover="this.style.background='#d0d7e6'" onmouseout="this.style.background='#eef2fa'"><span>📍</span> เดือนนี้</button>
          
          <div style="display: flex; align-items: center; background: white; border: 1px solid var(--gray-300); border-radius: var(--border-radius-sm); overflow: hidden; box-shadow: 0 1px 2px rgba(0,0,0,0.05);">
            <button onclick="shiftGlobalMonth(-1)" style="border:none; background:transparent; padding: 8px 12px; cursor: pointer; color: var(--primary); font-weight: bold; transition: background 0.2s;" onmouseover="this.style.background='#f0f2f5'" onmouseout="this.style.background='transparent'">&#10094;</button>
            <div style="position: relative; display: flex; align-items: center; justify-content: center; min-width: 120px; text-align: center; font-size: 0.95rem; font-weight: 700; color: var(--text-main); padding: 8px 12px; cursor: pointer; transition: background 0.2s;" onmouseover="this.style.background='#f0f2f5'" onmouseout="this.style.background='transparent'" title="เลือกเดือน" onclick="try { document.getElementById('global-month-picker').showPicker(); } catch(e) {}">
              ${displayMonthStr}
              <input type="month" id="global-month-picker" value="${state.selectedMonth}" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; opacity: 0; pointer-events: none;" onchange="updateGlobalMonth(this.value)">
            </div>
            <button onclick="shiftGlobalMonth(1)" style="border:none; background:transparent; padding: 8px 12px; cursor: pointer; color: var(--primary); font-weight: bold; transition: background 0.2s;" onmouseover="this.style.background='#f0f2f5'" onmouseout="this.style.background='transparent'">&#10095;</button>
          </div>
        </div>
        ${companyToggleHTML}
      </div>
    </div>
  `;
}

// Global month selector navigation
window.goToCurrentMonth = function () {
  const now = new Date();
  const currentMonthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  if (state.selectedMonth !== currentMonthStr) {
    const picker = document.getElementById('global-month-picker');
    if (picker) picker.value = currentMonthStr;
    updateGlobalMonth(currentMonthStr);
  }
};

window.shiftGlobalMonth = function (direction) {
  if (!state.selectedMonth) return;
  const [yearStr, monthStr] = state.selectedMonth.split('-');
  let date = new Date(parseInt(yearStr), parseInt(monthStr) - 1, 1);
  date.setMonth(date.getMonth() + direction);

  const newYear = date.getFullYear();
  const newMonth = String(date.getMonth() + 1).padStart(2, '0');
  const newValue = `${newYear}-${newMonth}`;

  const picker = document.getElementById('global-month-picker');
  if (picker) {
    picker.value = newValue;
  }
  updateGlobalMonth(newValue);
};

// Global month selector update callback
window.updateGlobalMonth = async function (val) {
  const oldYear = state.currentCalendarYear;
  state.selectedMonth = val;
  if (val) {
    const yearStr = val.split('-')[0];
    state.currentCalendarYear = parseInt(yearStr);
  }

  if (oldYear !== state.currentCalendarYear) {
    await loadCompanyContext();
  }

  // Check which modals are currently active
  const isTimesheetOpen = document.getElementById('timesheet-grid-modal-overlay')?.classList.contains('active');
  const isPayslipOpen = document.getElementById('payslip-modal-overlay')?.classList.contains('active');

  // Close all modals to avoid stale UI state
  document.querySelectorAll('.modal-overlay').forEach(el => el.classList.remove('active'));

  // Re-navigate to refresh main content
  await navigateTo(state.activePage);

  // If a modal was open, reload it with the new month's data
  if (isTimesheetOpen && state.activeEditingEmployeeId) {
    await openEmployeeTimesheetGrid(state.activeEditingEmployeeId);
  } else if (isPayslipOpen && state.activePayslipEmployeeId) {
    await showPayslipModal(state.activePayslipEmployeeId);
  }
};

// ==========================================================================
// RENDERING FUNCTIONS FOR ROUTE CONTAINERS
// ==========================================================================


// ==========================================================================
// DASHBOARD & SUMMARY (Extracted to src/modules/dashboard.js)
// ==========================================================================

// ==========================================================================
// EMPLOYEES MANAGEMENT (Extracted to src/modules/employees.js)
// ==========================================================================

// ==========================================================================
// ATTENDANCE & TIMESHEETS (Extracted to src/modules/timesheets.js)
// ==========================================================================
